import { APIOutput, AppError, ErrorCode } from "@amzn/ask-legal-domain";
import { APIResponse } from "../api/common";
import { UIConstants } from "./common-utils";

/**
 * Both variants declare `data` and `err` so callers can read `result.err` after `if (!result.ok)`.
 * This project compiles without `strictNullChecks`, and in that mode TypeScript does not narrow
 * the false branch of a boolean discriminant.
 */
export type ApiCallResult<O> =
    | { ok: true; data: O; err?: undefined }
    | { ok: false; data?: undefined; err: AppError };

/**
 * Component state for one API-loaded value. `idle` is for values that are only requested on demand
 * (nothing has been asked for yet); loads that start on mount begin at `loading`.
 */
export type Loadable<T> =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "error"; err: AppError }
    | { status: "loaded"; data: T };

/** Shape of the HTTP errors thrown by the Amplify REST client (axios-style). */
interface HttpErrorLike {
    response?: {
        status?: number;
        data?: { message?: string };
    };
}

const isHttpErrorLike = (e: unknown): e is HttpErrorLike =>
    typeof e === "object" && e !== null && "response" in e;

/**
 * Same mapping as the deprecated useAPI/useAPI2 hooks: the server's message and HTTP status
 * when present, otherwise a generic 500. Callers rely on these codes (403, 404, 409, ...).
 */
export function toAppError(e: unknown): AppError {
    let code = 500;
    let message = UIConstants.ERROR_MESSAGE;
    if (isHttpErrorLike(e)) {
        if (e.response?.data?.message) {
            message = e.response.data.message;
        }
        if (e.response?.status && e.response.status > 0) {
            code = e.response.status;
        }
    }
    return AppError.create(message, code as ErrorCode);
}

/**
 * Calls an API method directly (replacement for the deprecated useAPI/useAPI2 hooks) and
 * unwraps the APIOutput. Never throws: failures come back as `{ ok: false, err }`.
 */
export async function callApi<I, O>(
    api: (input: I) => Promise<APIResponse<O>>,
    input: I
): Promise<ApiCallResult<O>> {
    try {
        const raw = await api(input);
        const output = APIOutput.fromRaw<O>(raw.data);
        if (output.isErr()) {
            return { ok: false, err: output.err };
        }
        return { ok: true, data: output.data };
    } catch (e: unknown) {
        return { ok: false, err: toAppError(e) };
    }
}
